import { PORTRAIT_TABLET_MAX_WIDTH } from "@/src/common/config"
import { parseSearchParam } from "@/src/common/utils"
import { NavigationMenuItem } from "@/src/sb/business-layer/types"
import { EventTrackOptions } from "@/src/sb/components/Analytics/AnalyticsAdapter"
import Avatar from "@/src/sb/components/Avatar"
import MenuDropdown from "@/src/sb/components/Navigationbar/MenuDropdown"
import Typography from "@/src/sb/components/Typography"
import { useUser } from "@/src/sb/hooks/useUser"
import { useWindowSize } from "@/src/sb/hooks/useWindowSize"
import { useRouter } from "next/router"
import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"

import { HELP_LINK } from "../../../constants/configs"
import { navItems, pages } from "../../../constants/copy"
import {
  MenuItemOption,
  MenuItemSeparator,
  authOptions
} from "../../../constants/copy/navItems"
import { MobileNavItem, NAV_TYPES } from "../../NavItem"
import { MenuItem } from "./MenuItem"

const { accounts } = navItems

type MoreMenuProps = {
  onMenuItemClick?: (
    menuItem: Pick<
      NavigationMenuItem,
      "label" | "destination" | "category_name"
    >
  ) => void
  navigationMenu: NavigationMenuItem[]
}

const Menu = ({
  renderDropdown
}: {
  renderDropdown: (onClose: () => void) => JSX.Element
}) => {
  const [, currentWidth] = useWindowSize()
  const [showMenu, setShowMenu] = useState(false)

  const { label, src } = NAV_TYPES["moreMenu"]

  return (
    <section className={"inline-block"}>
      <button
        type="button"
        className={
          "cursor-pointer bg-stears_black py-3.5 min-w-[calc(100vw/4)] inline-block text-stears_white"
        }
        onClick={() => setShowMenu(!showMenu)}
        aria-expanded={showMenu}
        aria-haspopup={true}
        aria-hidden={!showMenu}
        aria-label={"More Topics"}
      >
        <div className="flex justify-center relative">
          <Avatar size="sm" src={src} alt={label} dotRing={false} />
        </div>
        <div className="mt-2 text-gray-400">
          <Typography type="btn_12">{label}</Typography>
        </div>
      </button>

      {/* 1024, gotten from "screens.portrait_tablet" modifier*/}
      {showMenu && currentWidth <= PORTRAIT_TABLET_MAX_WIDTH
        ? createPortal(
            renderDropdown(() => setShowMenu(false)),
            document.querySelector("#reactPortal")!
          )
        : null}
    </section>
  )
}

const MoreMenu = (props: MoreMenuProps) => {
  const { navigationMenu, onMenuItemClick } = props

  const menuItemsWithCategoryName: Array<NavigationMenuItem> = navigationMenu
    .filter(({ category_name }) => Boolean(category_name))
    .filter(({ more_topics_visibility_by_screen }) =>
      more_topics_visibility_by_screen.includes("mobile")
    )

  return (
    <Menu
      renderDropdown={(onClose) => (
        <MenuDropdown
          categorizeMenuItem
          onClose={onClose}
          onMenuItemClick={({ label, destination, category_name }) =>
            onMenuItemClick?.({
              label,
              destination,
              category_name
            })
          }
          menu={menuItemsWithCategoryName.map((menuItem) => ({
            ...menuItem,
            href: menuItem.destination
          }))}
        />
      )}
    />
  )
}

type AccountProps = {
  userType: keyof typeof accounts
  onClick?: (label: string) => void
  setNextQueryParam?: boolean
}

export const Account = (props: AccountProps) => {
  const { asPath = {} } = useRouter()
  const user = useUser()
  const { userType, onClick, setNextQueryParam } = props

  const HAS_PRO_ROLE = user?.data?.roles?.includes("pro")

  const menuItems = accounts[userType]
  // avoid duplicate menu
  const menuAlreadyExists = (
    menuItem: MenuItemOption,
    menuItems: MenuItemOption[]
  ) => {
    return menuItems.some((item) => {
      if ("type" in item && item.type === "separator") return false
      if ("type" in menuItem && menuItem.type === "separator") return false
      const linkItem = item as Exclude<typeof item, MenuItemSeparator>
      const linkMenuItem = menuItem as Exclude<
        typeof menuItem,
        MenuItemSeparator
      >
      return linkItem.label === linkMenuItem.label
    })
  }
  useEffect(() => {
    // push admin menu item to the 2nd position (only if feature flag is enabled)
    if (menuAlreadyExists(authOptions.admin, menuItems)) return
    if (user?.data?.isAdmin && HAS_PRO_ROLE) {
      menuItems.splice(1, 0, authOptions.admin)
    }
  })

  return (
    <MobileNavItem.Dropdown type="account" menuAlign="right">
      <MenuItem
        menuItems={menuItems.map((item) => {
          if ("type" in item && item.type === "separator") {
            return item
          }
          const linkItem = item as Exclude<typeof item, MenuItemSeparator>
          return {
            ...linkItem,
            href:
              setNextQueryParam && linkItem?.canHaveNextQueryParam
                ? `${linkItem?.href}?${parseSearchParam({
                    next: asPath
                  }).toString()}`
                : linkItem?.href
          }
        })}
        onClick={onClick}
        trackingContext={"mobile_navbar"}
      />
    </MobileNavItem.Dropdown>
  )
}

export type MobileNavbarProps = {
  userType: keyof typeof accounts
  /**
   * @deprecated tracking analytics should be local to the component
   * @param name
   * @param props
   */
  onNavItemClick?: (
    name: string,
    props?: EventTrackOptions["eventProperties"]
  ) => void
  navigationMenu?: NavigationMenuItem[]
  setNextQueryParam?: boolean
}

export const MobileNavbar = (props: MobileNavbarProps) => {
  const { userType, navigationMenu, onNavItemClick, setNextQueryParam } = props

  const navItemClick = (name: string, props?: Record<string, any>) => () => {
    onNavItemClick?.(name, { ...props, context: "mobile_navbar" })
  }

  return (
    <div className="flex w-full justify-evenly bg-stears_black">
      <MobileNavItem.Link
        href={pages.home}
        type="home"
        onClick={navItemClick("home")}
      />
      {navigationMenu?.length ? (
        <MoreMenu
          navigationMenu={navigationMenu}
          onMenuItemClick={({ label, destination, category_name }) =>
            navItemClick("menu_item", {
              label,
              destination,
              category_name
            })()
          }
        />
      ) : null}
      <MobileNavItem.Link
        href={HELP_LINK}
        type="help"
        onClick={navItemClick("help")}
      />

      <Account
        userType={userType}
        onClick={navItemClick}
        setNextQueryParam={setNextQueryParam}
      />
    </div>
  )
}

export default MobileNavbar
